import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import { toast } from 'sonner';

const AccountDeactivated = () => {
  const { currentUser, accountStatus, logout, setCurrentUser, setAccountStatus } = useAuth();
  const navigate = useNavigate();
  const [isRequesting, setIsRequesting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  // Debug log account status
  console.log('AccountDeactivated: accountStatus =', accountStatus);

  const isSelfDeactivated = accountStatus?.selfDeactivated || accountStatus?.canSelfReactivate;
  
  // Function to check if account has been reactivated
  const checkAccountStatus = useCallback(async (showToast = true) => {
    setIsCheckingStatus(true);
    try {
      const response = await authService.getCurrentUser();
      const userData = response.data || response;
      
      // If account is active (no deactivation status or isActive is true)
      if (!response.accountStatus?.isDeactivated) {
        setCurrentUser(userData);
        setAccountStatus(null); // Clear deactivation status
        toast.success('Your account has been reactivated! Redirecting...');
        setTimeout(() => {
          navigate('/');
        }, 1500);
        return true;
      } else if (showToast) {
        toast.info('Account is still deactivated');
      }
      setLastChecked(new Date());
      return false;
    } catch (error) {
      console.error('Error checking account status:', error);
      if (showToast) {
        toast.error('Failed to check account status');
      }
      return false;
    } finally {
      setIsCheckingStatus(false);
    }
  }, [navigate, setCurrentUser, setAccountStatus]);

  // Auto-check account status every 30 seconds (for admin-deactivated accounts waiting for reactivation)
  useEffect(() => {
    // Only auto-check for admin-deactivated accounts (they can't self-reactivate)
    if (!isSelfDeactivated) {
      const interval = setInterval(() => {
        checkAccountStatus(false); // Silent check
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [isSelfDeactivated, checkAccountStatus]);

  const handleRequestReactivation = async () => {
    setIsRequesting(true);
    try {
      await authService.requestReactivation();
      setEmailSent(true);
      toast.success('Reactivation email sent! Check your inbox.');
    } catch (error) {
      if (error.supportRequired) {
        toast.error('Your account was deactivated by an administrator. Please contact support.');
      } else {
        toast.error(error.message || 'Failed to send reactivation email');
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-8 text-center ${isSelfDeactivated ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-red-500 to-red-600'}`}>
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
              {isSelfDeactivated ? (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              )}
            </div>
            <h1 className="text-2xl font-bold text-white">Account Deactivated</h1>
            <p className="text-white/80 mt-2">
              {isSelfDeactivated 
                ? 'You deactivated your account' 
                : 'Your account has been deactivated by an administrator'}
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6">
            {/* User Info */}
            {currentUser && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                  {currentUser.profileImage && currentUser.profileImage !== 'default-avatar.jpg' ? (
                    <img src={currentUser.profileImage} alt={currentUser.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-lg">{(currentUser.name || '?').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{currentUser.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{currentUser.email}</p>
                </div>
              </div>
            )}

            {/* Deactivation Details */}
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 text-sm">
                <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-gray-500 dark:text-gray-400">Deactivated on</p>
                  <p className="text-gray-900 dark:text-white font-medium">{formatDate(accountStatus?.deactivatedAt)}</p>
                </div>
              </div>

              {accountStatus?.reason && (
                <div className="flex items-start gap-3 text-sm">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-gray-500 dark:text-gray-400">Reason</p>
                    <p className="text-gray-900 dark:text-white font-medium">{accountStatus.reason || accountStatus.deactivationReason}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Section */}
            {isSelfDeactivated ? (
              <div className="space-y-4">
                {!emailSent ? (
                  <>
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">Ready to come back?</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                            Click below to receive a reactivation link via email. Your account will be restored once you verify.
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleRequestReactivation}
                      disabled={isRequesting}
                      className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    >
                      {isRequesting ? (
                        <>
                          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending Email...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Send Reactivation Email
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                    <div className="flex gap-3">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-green-800 dark:text-green-300 font-medium">Email Sent!</p>
                        <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                          Check your inbox for the reactivation link. The link expires in 24 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <div className="flex gap-3">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm text-red-800 dark:text-red-300 font-medium">Administrator Action Required</p>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                        Your account was deactivated by a platform administrator. To request reactivation, please contact our support team.
                      </p>
                    </div>
                  </div>
                </div>

                <a
                  href="mailto:support@Tasklina.com?subject=Account%20Reactivation%20Request"
                  className="w-full py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  Contact Support
                </a>

                {/* Check if reactivated button */}
                <button
                  onClick={() => checkAccountStatus(true)}
                  disabled={isCheckingStatus}
                  className="w-full py-3 px-4 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-xl font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isCheckingStatus ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Check if Reactivated
                    </>
                  )}
                </button>
                {lastChecked && (
                  <p className="text-xs text-center text-gray-400 dark:text-gray-500">
                    Last checked: {lastChecked.toLocaleTimeString()}
                  </p>
                )}
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full mt-4 py-3 px-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
          Need help? <a href="mailto:support@Tasklina.com" className="text-blue-600 dark:text-blue-400 hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
};

export default AccountDeactivated;
